import { useEffect, useState } from "react";
import { Download, FlaskConical, RefreshCw, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../../lib/api";
import { Button } from "../ui/button";
import CriticalPinDialog from "../CriticalPinDialog";

export default function TestDataTab() {
    const [data, setData] = useState(null);
    const [pinOpen, setPinOpen] = useState(false);
    const [restoreFile, setRestoreFile] = useState(null);
    const [restorePinOpen, setRestorePinOpen] = useState(false);

    const load = () => api.get("/admin/test-data/summary").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);

    const cleanup = async (pin) => {
        try {
            const r = await api.post("/admin/test-data/cleanup", {}, { headers: { "X-Critical-Pin": pin } });
            toast.success(`${r.data.total} test kaydı kalıcı olarak silindi`);
            setPinOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Temizlik başarısız");
        }
    };

    const restore = async (pin) => {
        try {
            const fd = new FormData();
            fd.append("file", restoreFile);
            const r = await api.post("/admin/test-data/restore", fd, { headers: { "X-Critical-Pin": pin, "Content-Type": "multipart/form-data" } });
            toast.success(`${r.data.restored} kayıt geri yüklendi (${r.data.skipped} mevcut kayıt atlandı)`);
            setRestoreFile(null);
            setRestorePinOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Geri yükleme başarısız");
        }
    };

    const items = data ? Object.entries(data.items) : [];
    const total = data?.total || 0;

    return (
        <div className="space-y-4" data-testid="admin-testdata-tab">
            <div className="flex flex-wrap items-center gap-3 rounded-2xl border bg-card p-4">
                <FlaskConical className="h-6 w-6 text-terra-600" />
                <div className="text-sm">
                    <p className="font-semibold">Test Kayıtları</p>
                    <p className="text-xs text-muted-foreground">
                        Demo hesaplara ait işlemler ve içeriğinde "test"/"deneme" geçen kayıtlar tespit edilir. Silme işlemi <span className="font-semibold text-red-600">kalıcıdır</span> — kayıtlar Geri Dönüşüm Kutusu'na taşınmaz. Silmeden önce yedek almanız önerilir.
                    </p>
                </div>
                <div className="ml-auto flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={load} data-testid="testdata-refresh"><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Yenile</Button>
                    {total > 0 && (
                        <a href={`${API}/admin/test-data/export`} className="inline-flex h-8 items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-muted" data-testid="testdata-export-button">
                            <Download className="h-3.5 w-3.5" /> Yedeği İndir
                        </a>
                    )}
                    <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border px-3 text-xs font-medium hover:bg-muted" data-testid="testdata-restore-button">
                        <input type="file" accept=".json" className="hidden" onChange={(e) => { if (e.target.files[0]) { setRestoreFile(e.target.files[0]); setRestorePinOpen(true); } e.target.value = ""; }} data-testid="testdata-restore-input" />
                        <Upload className="h-3.5 w-3.5" /> Yedekten Yükle
                    </label>
                    {total > 0 && (
                        <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => setPinOpen(true)} data-testid="testdata-cleanup-button">
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Test Kayıtlarını Sil
                        </Button>
                    )}
                </div>
            </div>

            {data === null ? (
                <div className="h-40 animate-pulse rounded-2xl bg-muted" />
            ) : total === 0 ? (
                <div className="rounded-2xl border border-dashed p-10 text-center" data-testid="testdata-empty">
                    <FlaskConical className="mx-auto h-8 w-8 text-muted-foreground/50" />
                    <p className="mt-2 text-sm font-medium">Test kaydı bulunamadı</p>
                    <p className="text-xs text-muted-foreground">Sistemde demo hesap işlemi veya "test"/"deneme" işaretli kayıt yok.</p>
                </div>
            ) : (
                <div className="overflow-hidden rounded-2xl border bg-card shadow-sm">
                    <div className="flex items-center justify-between border-b bg-red-50 px-4 py-2.5" data-testid="testdata-total">
                        <p className="text-sm font-semibold text-red-800">Test işlemleri tespit edildi</p>
                        <p className="text-xs font-bold text-red-700">Toplam {total} kayıt</p>
                    </div>
                    <table className="w-full text-sm">
                        <tbody>
                            {items.map(([col, v]) => (
                                <tr key={col} className="border-b last:border-0" data-testid={`testdata-row-${col}`}>
                                    <td className="p-3 font-medium">{v.label}</td>
                                    <td className="p-3 text-muted-foreground">{col}</td>
                                    <td className="p-3 text-right font-display font-extrabold text-red-600">{v.count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <CriticalPinDialog
                open={pinOpen}
                onClose={() => setPinOpen(false)}
                title="Test Kayıtlarını Sil"
                message={`Tespit edilen tüm test kayıtları (bağlı sevkiyat, fatura, teklif, mesaj, oturum gibi ilişkili kayıtlar dahil) kalıcı olarak silinecek. Kayıtlar Geri Dönüşüm Kutusu'na TAŞINMAZ. Bu işlem geri alınamaz. Toplam ${total} kayıt.`}
                onSubmit={cleanup}
            />
            <CriticalPinDialog
                open={restorePinOpen}
                onClose={() => { setRestorePinOpen(false); setRestoreFile(null); }}
                title="Yedekten Geri Yükle"
                message={`"${restoreFile?.name || ""}" dosyasındaki test kayıtları geri yüklenecek. Sistemde zaten var olan kayıtlar ezilmez (atlanır).`}
                onSubmit={restore}
            />
        </div>
    );
}
